import { useMemo, useState } from "react";
import { Table } from "reactstrap";

function ModuloDevuelveCertificados(props) {
  const [busqueda, setBusqueda] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "apellidoNombre",
    direction: "asc",
  });

  const normalizarTexto = (valor) =>
    String(valor ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        key,
        direction: "asc",
      };
    });
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const certificadosFiltradosYOrdenados = useMemo(() => {
    const datos = Array.isArray(props.cosas) ? [...props.cosas] : [];
    const textoBusqueda = normalizarTexto(busqueda);

    const filtrados = !textoBusqueda
      ? datos
      : datos.filter((certificado) => {
          const contenidoBuscable = [
            certificado?.dniCertificado,
            certificado?.grado,
            certificado?.apellidoNombre,
            certificado?.calidadDe,
            certificado?.sedeCertificado,
            certificado?.estado,
          ]
            .map(normalizarTexto)
            .join(" ");

          return contenidoBuscable.includes(textoBusqueda);
        });

    const { key, direction } = sortConfig;

    if (!key) return filtrados;

    return filtrados.sort((a, b) => {
      const valorA = a?.[key];
      const valorB = b?.[key];

      if (key === "dniCertificado") {
        const numeroA = Number(valorA) || 0;
        const numeroB = Number(valorB) || 0;

        return direction === "asc"
          ? numeroA - numeroB
          : numeroB - numeroA;
      }

      const textoA = normalizarTexto(valorA);
      const textoB = normalizarTexto(valorB);

      return direction === "asc"
        ? textoA.localeCompare(textoB, "es", { numeric: true })
        : textoB.localeCompare(textoA, "es", { numeric: true });
    });
  }, [props.cosas, busqueda, sortConfig]);

  const totalCertificados = Array.isArray(props.cosas)
    ? props.cosas.length
    : 0;

  const totalMostrados = certificadosFiltradosYOrdenados.length;

  const limpiarBusqueda = () => {
    setBusqueda("");
  };

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        padding: "16px",
        marginTop: "16px",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.10)",
        color: "#212529",
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "12px",
          marginBottom: "14px",
        }}
      >
        <div>
          <h5 style={{ margin: 0, fontWeight: 700 }}>
            Certificados cargados
          </h5>

          <small style={{ color: "#6c757d" }}>
            Mostrando {totalMostrados} de {totalCertificados}
          </small>
        </div>

        <div
          style={{
            display: "flex",
            width: "min(100%, 460px)",
            gap: "8px",
          }}
        >
          <input
            type="search"
            className="form-control"
            value={busqueda}
            onChange={(event) => setBusqueda(event.target.value)}
            placeholder="Buscar por DNI, nombre, jerarquía o condición..."
            aria-label="Buscar certificados"
          />

          {busqueda && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={limpiarBusqueda}
              title="Limpiar búsqueda"
            >
              Limpiar
            </button>
          )}
        </div>
      </div>

      <div
        style={{
          maxHeight: "430px",
          overflowY: "auto",
          borderRadius: "8px",
          border: "1px solid #dee2e6",
        }}
      >
        <Table
          responsive
          bordered
          hover
          size="sm"
          className="align-middle mb-0"
        >
          <thead
            className="table-light"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 2,
            }}
          >
            <tr>
              <th
                style={{
                  width: "55px",
                  textAlign: "center",
                }}
              >
                #
              </th>

              <th>
                <button
                  type="button"
                  className="btn btn-link text-dark text-decoration-none p-0 fw-bold"
                  onClick={() => handleSort("dniCertificado")}
                >
                  Identificación{" "}
                  <span style={{ color: "#6c757d" }}>
                    {sortIcon("dniCertificado")}
                  </span>
                </button>
              </th>

              <th>
                <button
                  type="button"
                  className="btn btn-link text-dark text-decoration-none p-0 fw-bold"
                  onClick={() => handleSort("grado")}
                >
                  Jerarquía{" "}
                  <span style={{ color: "#6c757d" }}>
                    {sortIcon("grado")}
                  </span>
                </button>
              </th>

              <th>
                <button
                  type="button"
                  className="btn btn-link text-dark text-decoration-none p-0 fw-bold"
                  onClick={() => handleSort("apellidoNombre")}
                >
                  Apellido y nombre{" "}
                  <span style={{ color: "#6c757d" }}>
                    {sortIcon("apellidoNombre")}
                  </span>
                </button>
              </th>

              <th>
                <button
                  type="button"
                  className="btn btn-link text-dark text-decoration-none p-0 fw-bold"
                  onClick={() => handleSort("calidadDe")}
                >
                  Condición{" "}
                  <span style={{ color: "#6c757d" }}>
                    {sortIcon("calidadDe")}
                  </span>
                </button>
              </th>

              <th
                style={{
                  minWidth: "175px",
                  textAlign: "center",
                }}
              >
                Acciones
              </th>
            </tr>
          </thead>

          <tbody>
            {certificadosFiltradosYOrdenados.length > 0 ? (
              certificadosFiltradosYOrdenados.map(
                (certificado, index) => (
                  <tr key={certificado?.id ?? `${certificado?.dniCertificado}-${index}`}>
                    <td className="text-center">{index + 1}</td>

                    <td>
                      <strong>
                        {certificado?.dniCertificado || "-"}
                      </strong>
                    </td>

                    <td>{certificado?.grado || "-"}</td>

                    <td>{certificado?.apellidoNombre || "-"}</td>

                    <td>
                      {certificado?.calidadDe ? (
                        <span className="badge bg-secondary">
                          {certificado.calidadDe}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          justifyContent: "center",
                          gap: "6px",
                        }}
                      >
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          onClick={() => props.myID(certificado)}
                          title="Imprimir certificado"
                        >
                          Imprimir
                        </button>

                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => props.desactiva(certificado)}
                          title="Eliminar certificado"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              )
            ) : (
              <tr>
                <td colSpan="6" className="text-center py-4">
                  <strong>No se encontraron certificados.</strong>

                  {busqueda && (
                    <div
                      style={{
                        marginTop: "6px",
                        color: "#6c757d",
                      }}
                    >
                      Probá con otro DNI, nombre, jerarquía o condición.
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

export default ModuloDevuelveCertificados;